import React, { useEffect, useState } from 'react';
import { BatteryFullIcon, CheckIcon, ChevronRightIcon, CoffeeIcon, DicesIcon, DropletsIcon, HeartPulseIcon, HomeIcon, MapPinIcon, NavigationIcon, PlusIcon, QrCodeIcon, ScanLineIcon, Share2Icon, ShoppingBagIcon, SignalIcon, SparklesIcon, ThumbsUpIcon, UserIcon, WifiIcon } from 'lucide-react';
const machineImage = 'https://cdn.magicpatterns.com/uploads/6oRRhxP26uY781MFuuxrmT/image.png';
type TabId = 'home' | 'drink' | 'scan' | 'map' | 'drops';
const journey: Array<{
  id: TabId;
  eyebrow: string;
  headline: string;
  detail: string;
}> = [{
  id: 'home',
  eyebrow: 'Time for a break',
  headline: 'SIP knows when the day needs a reset.',
  detail: 'A nudge at 3:10 PM, routed to the nearest JuLi™ endpoint — 40 feet away.'
}, {
  id: 'drink',
  eyebrow: 'Drink of the day',
  headline: 'A recommendation, not a menu.',
  detail: 'Chosen from what the member drinks, the time of day, and how they want to feel next.'
}, {
  id: 'scan',
  eyebrow: 'Scan and pour',
  headline: 'One tap at the machine.',
  detail: 'The membership authorizes the pour. No cash, no card, no per-drink decision.'
}];
const tabs: Array<{
  id: TabId;
  label: string;
  Icon: React.ElementType;
}> = [{
  id: 'home',
  label: 'Home',
  Icon: HomeIcon
}, {
  id: 'drink',
  label: 'For you',
  Icon: SparklesIcon
}, {
  id: 'scan',
  label: 'Scan',
  Icon: ScanLineIcon
}, {
  id: 'map',
  label: 'Map',
  Icon: MapPinIcon
}, {
  id: 'drops',
  label: 'Flavors',
  Icon: DropletsIcon
}];
type Recipe = {
  id: number;
  slot: string;
  name: string;
  color: string;
  summary: string;
  shared: boolean;
};
const initialRecipes: Recipe[] = [{
  id: 0,
  slot: 'Morning',
  name: 'Cold Brew',
  color: '#a78bfa',
  summary: 'Still · Ice cold · No stevia',
  shared: true
}, {
  id: 1,
  slot: 'Afternoon',
  name: 'Citrus Electrolyte',
  color: '#60a5fa',
  summary: 'Sparkling · Extra fizz · Ice cold · Light stevia',
  shared: false
}];
const drinkOptions: Array<{
  name: string;
  benefit: string;
  Icon: React.ElementType;
  accent: string;
}> = [{
  name: 'Citrus Electrolyte',
  benefit: 'Rehydrate after the gym',
  Icon: DropletsIcon,
  accent: '#60a5fa'
}, {
  name: 'Cold Brew · Half Caf',
  benefit: 'Focus without the crash',
  Icon: CoffeeIcon,
  accent: '#a78bfa'
}, {
  name: 'Ginger Sparkling',
  benefit: 'Settle and reset',
  Icon: HeartPulseIcon,
  accent: '#34d399'
}];
export function SipPlatformVisual() {
  const [tab, setTab] = useState<TabId>('home');
  const [autoplay, setAutoplay] = useState(true);
  const [pour, setPour] = useState(0);
  const [recipes, setRecipes] = useState<Recipe[]>(initialRecipes);
  useEffect(() => {
    if (!autoplay) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = window.setInterval(() => {
      setTab(current => {
        const index = journey.findIndex(item => item.id === current);
        return journey[(index + 1) % journey.length].id;
      });
    }, 4200);
    return () => window.clearInterval(interval);
  }, [autoplay]);
  useEffect(() => {
    if (tab !== 'scan') {
      setPour(0);
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPour(100);
      return;
    }
    const interval = window.setInterval(() => setPour(current => current >= 100 ? 100 : current + 3), 60);
    return () => window.clearInterval(interval);
  }, [tab]);
  const select = (next: TabId) => {
    setAutoplay(false);
    setTab(next);
  };
  const journeyIndex = journey.findIndex(item => item.id === tab);
  return <section className="grid min-h-0 min-w-0 flex-1 items-stretch gap-4 lg:grid-cols-[1.05fr_.95fr] lg:gap-8" aria-label="SIP — the SmartSoda Intelligence Platform app" data-id="element-1102" data-mp="14jcil4u5jdgz">
      <div className="flex min-h-0 min-w-0 flex-col justify-center" data-id="element-1103" data-mp="18k6bydqd5kh9">
        <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-700" data-id="element-1104" data-mp="1dldgm3x7m3jp">One install · hundreds of members</p>
        <p className="mt-1 text-lg font-bold leading-tight text-navy-950 sm:text-xl" data-id="element-1105" data-mp="1ijexci75xqs0">
          Facilities signs the contract. SIP builds the relationship with everyone who works there.
        </p>

        <ol className="mt-3 flex flex-col gap-1.5" data-id="element-1106" data-mp="zx1zax6zxd1b">
          {journey.map((item, index) => {
          const isActive = item.id === tab;
          return <li key={item.id} data-id="element-1107" data-mp="19o6cbrd42nti" data-mpi={index}>
                <button type="button" onClick={() => select(item.id)} aria-current={isActive} className={`flex w-full items-start gap-3 rounded-2xl border px-3.5 py-2.5 text-left transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${isActive ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-transparent bg-slate-50 hover:border-slate-200'}`} data-id="element-1108" data-mp="19nwvata1nqzy">
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold transition-colors duration-300 ${isActive ? 'bg-blue-500 text-white' : 'bg-white text-slate-400'}`} data-id="element-1109" data-mp="279kgsj4yoowp">
                    {index + 1}
                  </span>
                  <span className="min-w-0" data-id="element-1110" data-mp="2esbm4tkkd53a">
                    <span className={`block text-[9px] font-bold uppercase tracking-[.14em] ${isActive ? 'text-blue-700' : 'text-slate-400'}`} data-id="element-1111" data-mp="20v59lgqfo2mi">
                      {item.eyebrow}
                    </span>
                    <span className="mt-0.5 block text-[13px] font-bold leading-snug text-navy-950" data-id="element-1112" data-mp="2sljewokds6mk">{item.headline}</span>
                    {isActive && <span className="mt-1 block text-[11px] leading-snug text-slate-500" data-id="element-1113" data-mp="2xk4fip3josbj">{item.detail}</span>}
                  </span>
                </button>
              </li>;
        })}
        </ol>

        <div className="mt-3 grid gap-1.5 border-t border-slate-200 pt-3 sm:grid-cols-2" data-id="element-1114" data-mp="2nt005o9yj5g6">
          <button type="button" onClick={() => select('map')} className={`flex items-start gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${tab === 'map' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`} data-id="element-1115" data-mp="3bjdslfwgf6ba">
            <MapPinIcon className={`mt-0.5 h-4 w-4 shrink-0 ${tab === 'map' ? 'text-blue-600' : 'text-slate-400'}`} aria-hidden="true" data-id="element-1116" data-mp="3h54noyccv9yk" />
            <span className="min-w-0" data-id="element-1117" data-mp="378i4sf3lasz6">
              <span className="block text-[11px] font-bold text-navy-950" data-id="element-1118" data-mp="379jplbaeqtu3">Find an endpoint</span>
              <span className="block text-[10px] leading-snug text-slate-500" data-id="element-1119" data-mp="32gqlmpmrjpj2">In this building, and everywhere nearby</span>
            </span>
          </button>

          <button type="button" onClick={() => select('drops')} className={`flex items-start gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${tab === 'drops' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`} data-id="element-1120" data-mp="2th3mkahiavt6">
            <DropletsIcon className={`mt-0.5 h-4 w-4 shrink-0 ${tab === 'drops' ? 'text-blue-600' : 'text-slate-400'}`} aria-hidden="true" data-id="element-1121" data-mp="29hd68xta1fdp" />
            <span className="min-w-0" data-id="element-1122" data-mp="2je80h4iiotm6">
              <span className="block text-[11px] font-bold text-navy-950" data-id="element-1123" data-mp="2jb3ovm2q2vlh">Request a flavor</span>
              <span className="block text-[10px] leading-snug text-slate-500" data-id="element-1124" data-mp="2obhvr0sl7zhx">Members pull new flavors into their office</span>
            </span>
          </button>
        </div>

        <div className="mt-3 rounded-2xl bg-navy-950 px-3.5 py-3 text-white" data-id="element-1125" data-mp="1vodpytlo7h53">
          <p className="text-[9px] font-bold uppercase tracking-[.14em] text-blue-300" data-id="element-1126" data-mp="20mf4d185isa8">One contract, four compounding effects</p>

          <ol className="mt-2 grid grid-cols-2 gap-2" data-id="element-1127" data-mp="25eynedplyfmf">
            {[['Acquire', 'One install enrolls every employee in the building — no per-member sales cost'], ['Engage', 'The break prompt turns an occasional drink into a daily habit'], ['Measure', 'The employer sees hydration and sustainability data they never had'], ['Entrench', 'Recipes, history, and preferences make removal a staff issue, not a spend issue']].map(([term, detail], index) => <li key={term} className="flex gap-2" data-id="element-1128" data-mp="30ze4kgqxrrvm" data-mpi={index}>
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-blue-500 text-[8px] font-bold text-white" data-id="element-1129" data-mp="2w1w2nabc6cx4">
                  {index + 1}
                </span>
                <span className="min-w-0" data-id="element-1130" data-mp="36025bu4yvn31">
                  <span className="block text-[10px] font-bold leading-tight text-blue-100" data-id="element-1131" data-mp="35z0of919s6de">{term}</span>
                  <span className="mt-0.5 block text-[9px] leading-snug text-slate-400" data-id="element-1132" data-mp="3et5c5jl3x1op">{detail}</span>
                </span>
              </li>)}
          </ol>

          <p className="mt-2.5 border-t border-white/10 pt-2 text-[10px] font-semibold leading-snug text-blue-200" data-id="element-1133" data-mp="3tqo2urhchoys">
            Competitors have to win a purchase order. We only have to keep the workforce.
          </p>
        </div>

        <p className="mt-2 text-[10px] font-semibold text-blue-700" data-id="element-1134" data-mp="g92cw1ghav85">Tap the phone — every tab is live.</p>
      </div>

      {/* Device */}
      <div className="flex min-h-0 min-w-0 items-center justify-center" data-id="element-1135" data-mp="28riiofymyi8">
        <div className="relative mx-auto w-full max-w-[232px]" data-id="element-1136" data-mp="784epzsq1hxh">
          <div className="relative flex aspect-[9/19.5] w-full flex-col overflow-hidden rounded-[2.5rem] border-[7px] border-navy-950 bg-white shadow-2xl" data-id="element-1137" data-mp="7agymii1i2d6">
            <div className="relative flex items-center justify-between px-5 pb-1 pt-2.5 text-[9px] font-bold text-navy-950" data-id="element-1138" data-mp="3tgnmaxhfuoii">
              <span className="tabular-nums" data-id="element-1139" data-mp="3fqaagk1fepnw">3:10</span>
              <span className="absolute left-1/2 top-1.5 h-4 w-16 -translate-x-1/2 rounded-full bg-navy-950" aria-hidden="true" data-id="element-1140" data-mp="3kmr3nsnhfu7u" />
              <span className="flex items-center gap-1" data-id="element-1141" data-mp="35qt9gfx4czy9">
                <SignalIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1142" data-mp="3apw3747nfjqx" />
                <WifiIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1143" data-mp="1de01ud7fz8na" />
                <BatteryFullIcon className="h-3 w-3" aria-hidden="true" data-id="element-1144" data-mp="1dcoxoca33yvn" />
              </span>
            </div>

            <div className="flex items-center justify-between px-4 pt-2" data-id="element-1145" data-mp="1ib09gsjxwjgi">
              <div className="min-w-0" data-id="element-1146" data-mp="13hdijsurkpnd">
                <p className="text-[9px] font-semibold text-slate-400" data-id="element-1147" data-mp="18ex83owwwoum">Good afternoon</p>
                <p className="truncate text-[13px] font-bold leading-tight text-navy-950" data-id="element-1148" data-mp="18gqfx7j9hf47">Maya Chen</p>
              </div>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500" data-id="element-1149" data-mp="213b644o03l66">
                <UserIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1150" data-mp="1mdn5l5nn8qji" />
              </span>
            </div>

            <div key={tab} className="flex min-h-0 flex-1 flex-col px-3.5 pt-2.5 [animation:pd3-rise-in_.4s_ease-out]" data-id="element-1151" data-mp="1rcgj2y2xkhjo">
              {tab === 'home' && <BreakScreen onGo={() => select('map')} data-id="element-1152" data-mp="2f1iz61hx22c2" />}
              {tab === 'drink' && <DrinkScreen onScan={() => select('scan')} recipes={recipes} data-id="element-1153" data-mp="2jvdfqw28616x" />}
              {tab === 'scan' && <PourScreen progress={pour} data-id="element-1154" data-mp="8dij83hyp1p2" />}
              {tab === 'map' && <MapScreen data-id="element-1155" data-mp="3gs87ul0kwai" />}
              {tab === 'drops' && <FlavorScreen onScan={() => select('scan')} recipes={recipes} setRecipes={setRecipes} data-id="element-1156" data-mp="3dwaa69627m2" />}
            </div>

            {/* tab bar — live */}
            <nav className="mt-1 flex items-center justify-between border-t border-slate-100 px-3 py-2" aria-label="App navigation" data-id="element-1157" data-mp="d5jxfuyox2hq">
              {tabs.map(item => {
              const isActive = item.id === tab;
              if (item.id === 'scan') {
                return <button key={item.id} type="button" onClick={() => select(item.id)} aria-current={isActive} className={`flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[10px] font-bold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${isActive ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-id="element-1158" data-mp="3umju2hrrdmri">
                      <item.Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1159" data-mp="7hd2u5s5gn4g" /> {item.label}
                    </button>;
              }
              return <button key={item.id} type="button" onClick={() => select(item.id)} aria-label={item.label} aria-current={isActive} className={`rounded-lg p-1.5 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${isActive ? 'text-blue-600' : 'text-slate-300 hover:text-slate-500'}`} data-id="element-1160" data-mp="gjmg33esyulj">
                    <item.Icon className="h-4 w-4" aria-hidden="true" data-id="element-1161" data-mp="2x88byl25w48s" />
                  </button>;
            })}
            </nav>
          </div>

          <div className="mt-3 flex justify-center gap-1.5" aria-hidden="true" data-id="element-1162" data-mp="317r0dqt6yxgd">
            {journey.map((item, index) => <span key={item.id} className={`h-1.5 rounded-full transition-all duration-300 ${index === journeyIndex ? 'w-6 bg-blue-500' : 'w-1.5 bg-slate-300'}`} data-id="element-1163" data-mp="2oqy9mosoy5v5" data-mpi={index} />)}
          </div>
        </div>
      </div>
    </section>;
}
function BreakScreen({
  onGo
}: {
  onGo: () => void;
}) {
  return <>
      <div className="rounded-2xl bg-navy-950 px-3.5 py-3 text-white" data-id="element-1164" data-mp="2asrjjutzha61">
        <div className="flex items-center gap-2" data-id="element-1165" data-mp="26089v8yf2z6i">
          <span className="pd3-sip-pulse h-1.5 w-1.5 rounded-full bg-blue-400" aria-hidden="true" data-id="element-1166" data-mp="25ynbt9cw8y3w" />
          <p className="text-[9px] font-bold uppercase tracking-[.14em] text-blue-300" data-id="element-1167" data-mp="20z0y6yt5lfuk">Time for a break</p>
        </div>
        <p className="mt-1.5 text-[15px] font-bold leading-tight" data-id="element-1168" data-mp="1s0flhkad4v1p">You've been heads-down for 3 hours.</p>
        <p className="mt-1 text-[10px] leading-snug text-slate-400" data-id="element-1169" data-mp="1n1vtb256en0w">Stretch your legs — hydration is 40 feet away.</p>
      </div>

      <div className="mt-2.5 min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200" data-id="element-1170" data-mp="3n56jf8nzi28f">
        <div className="h-[104px] overflow-hidden bg-slate-50" data-id="element-1171" data-mp="3s1n6s0nd0ckg">
          <img src={machineImage} alt="A member filling a tumbler at a JuLi beverage platform" className="h-full w-full object-cover" loading="eager" decoding="async" data-id="element-1172" data-mp="3debu1vffymir" />
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-2" data-id="element-1173" data-mp="3iasn941hzr2p">
          <span className="min-w-0" data-id="element-1174" data-mp="38ez9jhc1lrjc">
            <span className="block truncate text-[11px] font-bold text-navy-950" data-id="element-1175" data-mp="38g0lrp4sok2n">JuLi™ · 4th floor lounge</span>
            <span className="block text-[9px] text-slate-500" data-id="element-1176" data-mp="2ughpbtlcix66">40 ft · 12 drinks available</span>
          </span>
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1177" data-mp="2zesy00zwn1mt" />
        </div>
      </div>

      <button type="button" onClick={onGo} className="mt-2.5 rounded-xl bg-blue-500 py-2.5 text-[11px] font-bold text-white transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400" data-id="element-1178" data-mp="slqifxemmi37">
        Take me there
      </button>
    </>;
}
function DrinkScreen({
  onScan,
  recipes
}: {
  onScan: () => void;
  recipes: Recipe[];
}) {
  return <>
      <div data-id="element-1179" data-mp="2rwurm5d0s6nt">
        <p className="text-[9px] font-bold uppercase tracking-[.14em] text-blue-700" data-id="element-1180" data-mp="2n41p7o50xaew">Drink of the day</p>
        <p className="mt-1 text-[15px] font-bold leading-tight text-navy-950" data-id="element-1181" data-mp="30vgci75yrxrr">How do you want to feel?</p>
      </div>

      <ul className="mt-2 flex flex-col gap-1.5" data-id="element-1182" data-mp="2vwv95yvf0xz3">
        {drinkOptions.map((drink, index) => <li key={drink.name} data-id="element-1183" data-mp="3au5s1tae164u" data-mpi={index}>
            <div className={`flex items-center gap-2.5 rounded-2xl border px-3 py-2 ${index === 0 ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`} data-id="element-1184" data-mp="3at44ipc6qr68">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-white" style={{
            backgroundColor: drink.accent
          }} data-id="element-1185" data-mp="1ggea86hwnfyb">
                <drink.Icon className="h-4 w-4" aria-hidden="true" data-id="element-1186" data-mp="214nm55v9d6x2" />
              </span>
              <span className="min-w-0" data-id="element-1187" data-mp="2170ca6b66nl7">
                <span className="block truncate text-[11px] font-bold text-navy-950" data-id="element-1188" data-mp="255hb82kfsrrj">{drink.name}</span>
                <span className="block truncate text-[9px] text-slate-500" data-id="element-1189" data-mp="2a4aobtvtagr6">{drink.benefit}</span>
              </span>
              {index === 0 && <span className="ml-auto shrink-0 rounded-full bg-blue-500 px-2 py-0.5 text-[8px] font-bold uppercase tracking-[.08em] text-white" data-id="element-1190" data-mp="2f22fumsjo0uu">
                  For you
                </span>}
            </div>
          </li>)}
      </ul>

      {/* Saved recipes */}
      <div className="mt-2.5 min-h-0 flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-id="element-1191" data-mp="2sxwp56x8cs8h">
        <div className="flex items-center justify-between gap-2" data-id="element-1192" data-mp="2xwr7y8v76gsl">
          <p className="text-[9px] font-bold uppercase tracking-[.12em] text-blue-700" data-id="element-1193" data-mp="32vkm83ib6btt">Your recipes</p>
          <span className="rounded-full bg-white px-1.5 py-0.5 text-[7px] font-bold text-slate-500" data-id="element-1194" data-mp="37tm26fkftv12">Saved to you</span>
        </div>

        <ul className="mt-1.5 flex flex-col gap-1" data-id="element-1195" data-mp="w3eczzr1fsff">
          {recipes.length === 0 ? <li className="rounded-lg border border-dashed border-slate-200 px-2 py-2 text-center text-[8px] text-slate-400" data-id="element-1196" data-mp="r90lw9jes7gk">
              Build one in Flavors
            </li> : recipes.map(recipe => <li key={recipe.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5" data-id="element-1197" data-mp="1ewrx19t7qeg9">
                <span className="flex min-w-0 items-center gap-1.5" data-id="element-1198" data-mp="19xxgyfmmr3zu">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{
              backgroundColor: recipe.color
            }} aria-hidden="true" data-id="element-1199" data-mp="155dsgnmemog9" />
                  <span className="min-w-0" data-id="element-1200" data-mp="1072imcw40dy1">
                    <span className="block truncate text-[9px] font-bold text-navy-950" data-id="element-1201" data-mp="1xmggo6rm68gb">
                      {recipe.slot} · {recipe.name}
                    </span>
                    <span className="block truncate text-[8px] text-slate-500" data-id="element-1202" data-mp="1stx339sxfdbi">{recipe.summary}</span>
                  </span>
                </span>
                <button type="button" onClick={onScan} className="flex shrink-0 items-center gap-1 rounded-full bg-navy-950 px-1.5 py-0.5 text-[7px] font-bold text-blue-100 transition-colors duration-200 hover:bg-navy-950/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400" data-id="element-1203" data-mp="15zey20au3fxf">
                  <ScanLineIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1204" data-mp="1oy0zxhe6db2e" /> Pour
                </button>
              </li>)}
        </ul>
      </div>

      <button type="button" onClick={onScan} className="mt-2 rounded-xl bg-blue-500 py-2.5 text-[11px] font-bold text-white transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400" data-id="element-1205" data-mp="1axzy9zq35zlv">
        Scan to pour this
      </button>
    </>;
}
function PourScreen({
  progress
}: {
  progress: number;
}) {
  const complete = progress >= 100;
  return <>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-center" data-id="element-1206" data-mp="3t5vr4i7xo5b2">
        <p className="text-[9px] font-bold uppercase tracking-[.14em] text-blue-700" data-id="element-1207" data-mp="3o72f6u8aomd0">Scan to pour</p>
        <span className="mx-auto mt-2 flex h-16 w-16 items-center justify-center rounded-2xl bg-navy-950 text-white" data-id="element-1208" data-mp="5wz36otq5wh0">
          <QrCodeIcon className="h-9 w-9" aria-hidden="true" data-id="element-1209" data-mp="39fsdmsr7i56y" />
        </span>
        <p className="mt-2 text-[10px] font-semibold text-slate-500" data-id="element-1210" data-mp="39dz1wz1qkus3">JuLi™ 4F-02 · authorized</p>
      </div>

      <div className="mt-2.5 min-h-0 flex-1 rounded-2xl bg-navy-950 px-3.5 py-3 text-white" data-id="element-1211" data-mp="35cnf11a8ynnl">
        <div className="flex items-center justify-between" data-id="element-1212" data-mp="3j7prk79lnb9d">
          <p className="text-[11px] font-bold" data-id="element-1213" data-mp="3eed3pzcqv8ho">Citrus Electrolyte</p>
          {complete ? <span className="flex items-center gap-1 rounded-full bg-blue-400 px-2 py-0.5 text-[8px] font-bold text-navy-950" data-id="element-1214" data-mp="2qo9c6qctyz71">
              <CheckIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1215" data-mp="2loekwlp587lg" /> Poured
            </span> : <span className="text-[9px] font-semibold tabular-nums text-blue-200" data-id="element-1216" data-mp="1i3yk482wma98">{progress}%</span>}
        </div>

        <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/15" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Pour progress" data-id="element-1217" data-mp="1rvumvwfxqojs">
          <div className="h-full rounded-full bg-blue-400 transition-[width] duration-75 ease-linear" style={{
          width: `${progress}%`
        }} data-id="element-1218" data-mp="20wso66yzohyo" />
        </div>

        <dl className="mt-2.5 grid grid-cols-2 gap-1.5" data-id="element-1219" data-mp="1wufo9z6xbe9t">
          <div className="rounded-lg bg-white/5 px-2 py-1.5" data-id="element-1220" data-mp="2auqcsvkrggrt">
            <dt className="text-[8px] font-bold uppercase tracking-[.08em] text-slate-400" data-id="element-1221" data-mp="2asw2scb1292i">Charged</dt>
            <dd className="text-[11px] font-bold text-blue-100" data-id="element-1222" data-mp="25tsvel7krqrd">$0.00</dd>
          </div>
          <div className="rounded-lg bg-white/5 px-2 py-1.5" data-id="element-1223" data-mp="gh7jjxxrjkcb">
            <dt className="text-[8px] font-bold uppercase tracking-[.08em] text-slate-400" data-id="element-1224" data-mp="bintpmg1toeo">Covered by</dt>
            <dd className="text-[11px] font-bold text-blue-100" data-id="element-1225" data-mp="2n699dx2zz1fg">Membership</dd>
          </div>
        </dl>

        <p className="mt-2 text-[9px] leading-snug text-slate-400" data-id="element-1226" data-mp="2s2zp2jaw3v0c">Pour logged · preference strengthened · cartridge level updated.</p>

        <div className="mt-2.5 border-t border-white/10 pt-2.5" data-id="element-1227" data-mp="2ddkb63wgjk22">
          <p className="text-[8px] font-bold uppercase tracking-[.12em] text-blue-300" data-id="element-1228" data-mp="2id6lo5kwim76">Bottles saved</p>

          <div className="mt-1.5 flex flex-col gap-1.5" data-id="element-1229" data-mp="24d5de52b4cho">
            <div className="rounded-lg bg-white/5 px-2 py-1.5" data-id="element-1230" data-mp="29ecke1wml7na">
              <div className="flex items-baseline justify-between gap-2" data-id="element-1231" data-mp="29bqfyb51789a">
                <p className="text-[9px] font-semibold text-slate-300" data-id="element-1232" data-mp="1ui3p1bfuveg5">You</p>
                <p className="text-[13px] font-bold tabular-nums text-white" data-id="element-1233" data-mp="1zgomx47mxm1f">{complete ? 215 : 214}</p>
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/10" data-id="element-1234" data-mp="3ov38jfncwssp">
                <div className="h-full rounded-full bg-blue-400 transition-[width] duration-500 ease-out" style={{
                width: '43%'
              }} data-id="element-1235" data-mp="3tsd7blwmygt7" />
              </div>
            </div>

            <div className="rounded-lg bg-white/5 px-2 py-1.5" data-id="element-1236" data-mp="3ncv6i3tehdze">
              <div className="flex items-baseline justify-between gap-2" data-id="element-1237" data-mp="3nfh9dq5cj5ba">
                <p className="text-[9px] font-semibold text-slate-300" data-id="element-1238" data-mp="3ietlvegbt6lt">This office</p>
                <p className="text-[13px] font-bold tabular-nums text-white" data-id="element-1239" data-mp="3ee1d0mbd93q2">{complete ? '18,431' : '18,430'}</p>
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/10" data-id="element-1240" data-mp="ezp7xcjhz5rd">
                <div className="h-full rounded-full bg-blue-400 transition-[width] duration-500 ease-out" style={{
                width: '78%'
              }} data-id="element-1241" data-mp="9z1harz6kqxo" />
              </div>
            </div>
          </div>

          <p className="mt-1.5 text-[8px] leading-snug text-slate-400" data-id="element-1242" data-mp="15etbo17srz1">Single-use bottles avoided at JuLi™ 4F-02 since install.</p>
        </div>
      </div>
    </>;
}
function MapScreen() {
  const inBuilding: Array<[string, string, string]> = [['4th floor lounge', '40 ft · 12 flavors', 'Closest'], ['Ground floor café', '2 min walk', 'Open'], ['7th floor pantry', '3 min walk', 'Busy']];
  const nearby: Array<[string, string]> = [['Equinox Bryant Park', '0.4 mi'], ['Hudson Yards food hall', '0.6 mi'], ['NYU Kimmel Center', '0.9 mi']];
  return <>
      <div className="relative h-[124px] shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100" data-id="element-1243" data-mp="2pkyb78dr7wm2">
        {/* stylized building map */}
        <span className="absolute inset-0" aria-hidden="true" data-id="element-1244" data-mp="2zi2wl5zrzo1t">
          <span className="absolute left-3 top-3 h-16 w-20 rounded-lg border border-slate-300 bg-white" data-id="element-1245" data-mp="2zg8b9qkldtx7" />
          <span className="absolute right-3 top-4 h-12 w-16 rounded-lg border border-slate-300 bg-white" data-id="element-1246" data-mp="349kvloi8nek5" />
          <span className="absolute bottom-3 left-6 h-10 w-24 rounded-lg border border-slate-300 bg-white" data-id="element-1247" data-mp="38ch7yxh6qefn" />
          <span className="absolute left-0 top-1/2 h-[3px] w-full -translate-y-1/2 bg-slate-300/70" data-id="element-1248" data-mp="3d7mv1zmbpz5b" />
          <span className="absolute left-1/2 top-0 h-full w-[3px] -translate-x-1/2 bg-slate-300/70" data-id="element-1249" data-mp="3i67z6a4p52z1" />
        </span>

        {/* member + endpoints */}
        <span className="pd3-sip-pulse absolute left-[24%] top-[58%] h-3 w-3 rounded-full border-2 border-white bg-navy-950" aria-hidden="true" data-id="element-1250" data-mp="2zlz7rettc2cb" />
        {[['32%', '30%'], ['66%', '36%'], ['52%', '74%']].map(([left, top]) => <span key={`${left}-${top}`} className="absolute flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-blue-500 text-white shadow" style={{
        left,
        top
      }} aria-hidden="true" data-id="element-1251" data-mp="2lmfzmm104r00">
            <DropletsIcon className="h-3 w-3" data-id="element-1252" data-mp="2qm2fl387slci" />
          </span>)}

        <span className="absolute bottom-2 left-2 rounded-full bg-white/95 px-2 py-0.5 text-[8px] font-bold text-navy-950" data-id="element-1253" data-mp="2qo5e6bnfnmsu">
          One Bryant Park · 3 endpoints
        </span>
        <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-blue-600 shadow" aria-hidden="true" data-id="element-1254" data-mp="5vboiiere70i">
          <NavigationIcon className="h-3 w-3" data-id="element-1255" data-mp="ie5zktttks5z" />
        </span>
      </div>

      <div className="mt-2 min-h-0 flex-1 overflow-hidden" data-id="element-1256" data-mp="ic2xhbfe78kw">
        <p className="text-[8px] font-bold uppercase tracking-[.12em] text-blue-700" data-id="element-1257" data-mp="4fziuqh058tj">In your building</p>
        <ul className="mt-1 flex flex-col gap-1" data-id="element-1258" data-mp="8h1bupkbson2">
          {inBuilding.map(([name, meta, status], index) => <li key={name} className={`flex items-center justify-between gap-2 rounded-lg border px-2 py-1.5 ${index === 0 ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`} data-id="element-1259" data-mp="w6m45z2dcy90" data-mpi={index}>
              <span className="min-w-0" data-id="element-1260" data-mp="nd0q6hy13l0n">
                <span className="block truncate text-[10px] font-bold text-navy-950" data-id="element-1261" data-mp="rf437b0cixo1">{name}</span>
                <span className="block text-[8px] text-slate-500" data-id="element-1262" data-mp="1f4q3y2wd9ky5">{meta}</span>
              </span>
              <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[7px] font-bold uppercase ${index === 0 ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-1263" data-mp="1jvetvdsee9v6">{status}</span>
            </li>)}
        </ul>

        <p className="mt-2 text-[8px] font-bold uppercase tracking-[.12em] text-slate-400" data-id="element-1264" data-mp="1ppk06up4nao4">Nearby on the network</p>
        <ul className="mt-1 flex flex-col gap-1" data-id="element-1265" data-mp="1kpffstl3lvsm">
          {nearby.map(([name, distance]) => <li key={name} className="flex items-center justify-between gap-2 px-0.5" data-id="element-1266" data-mp="1fquhizpepm6t">
              <span className="flex min-w-0 items-center gap-1.5" data-id="element-1267" data-mp="1atkt9ndr7gkk">
                <MapPinIcon className="h-2.5 w-2.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1268" data-mp="1622tv3rdpo1p" />
                <span className="truncate text-[10px] font-semibold text-navy-950" data-id="element-1269" data-mp="11zzfo7dbs5cq">{name}</span>
              </span>
              <span className="shrink-0 text-[9px] tabular-nums text-slate-500" data-id="element-1270" data-mp="x1o3vr3gzkrv">{distance}</span>
            </li>)}
        </ul>
      </div>
    </>;
}
const availableFlavors: Array<[string, string]> = [['Citrus Electrolyte', '#60a5fa'], ['Cold Brew', '#a78bfa'], ['Ginger Sparkling', '#34d399'], ['Yuzu Soda', '#fbbf24'], ['Peach Green Tea', '#f472b6'], ['Sparkling Mineral', '#22d3ee']];
const waterTypes = ['Still', 'Sparkling', 'Hot'] as const;
const fizzLevels = ['Light', 'Medium', 'Extra'] as const;
const chillLevels = ['Ambient', 'Cool', 'Ice cold'] as const;
const steviaLevels = ['None', 'Light', 'Full'] as const;
const shopItems: Array<[string, string, string]> = [['Microdose™ 6-pack', 'Your saved flavors, for JuLi Home', '$34'], ['Mango Chili cartridge', 'Not stocked at work — buy your own', '$8'], ['Insulated JuLi tumbler', '24 oz · scans at any endpoint', '$29'], ['JuLi Home starter', 'Countertop unit · members save 20%', '$249']];
function FlavorScreen({
  onScan,
  recipes,
  setRecipes
}: {
  onScan: () => void;
  recipes: Recipe[];
  setRecipes: React.Dispatch<React.SetStateAction<Recipe[]>>;
}) {
  const [base, setBase] = useState(0);
  const [water, setWater] = useState(1);
  const [fizz, setFizz] = useState(2);
  const [chill, setChill] = useState(2);
  const [stevia, setStevia] = useState(1);
  const [choosingSlot, setChoosingSlot] = useState(false);
  const [lucky, setLucky] = useState(false);
  const [cart, setCart] = useState<string[]>([]);
  const isSparkling = water === 1;
  const isHot = water === 2;
  const touch = (apply: () => void) => {
    apply();
    setChoosingSlot(false);
    setLucky(false);
  };
  const feelLucky = () => {
    setBase(1);
    setWater(1);
    setFizz(1);
    setChill(2);
    setStevia(1);
    setChoosingSlot(false);
    setLucky(true);
  };
  const summary = [waterTypes[water], isSparkling ? `${fizzLevels[fizz]} fizz` : null, isHot ? 'Hot' : chillLevels[chill], stevia === 0 ? 'No stevia' : `${steviaLevels[stevia]} stevia`].filter(Boolean).join(' · ');
  const requests: Array<[string, number]> = [['Mango Chili', 14], ['Matcha Latte', 9], ['Blood Orange', 6]];
  return <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pb-1" data-id="element-1271" data-mp="1scf4qa4gocuo">
      <div data-id="element-1272" data-mp="2bwmh2dfuppec">
        <p className="text-[9px] font-bold uppercase tracking-[.14em] text-blue-700" data-id="element-1273" data-mp="2bww3xs5lngft">On tap now</p>
        <p className="mt-0.5 text-[11px] font-bold text-navy-950" data-id="element-1274" data-mp="2fz0v8k8ks0za">JuLi™ · 4th floor lounge</p>
      </div>

      <ul className="flex flex-wrap gap-1" data-id="element-1275" data-mp="21xwxmg8b3wvz">
        {availableFlavors.map(([name, color], index) => <li key={name} data-id="element-1276" data-mp="26wrp06kcatrp" data-mpi={index}>
            <button type="button" onClick={() => touch(() => setBase(index))} aria-pressed={base === index} className={`flex items-center gap-1 rounded-full border px-2 py-1 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${base === index ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`} data-id="element-1277" data-mp="26w890ve92fgb">
              <span className="h-2 w-2 rounded-full" style={{
            backgroundColor: color
          }} aria-hidden="true" data-id="element-1278" data-mp="199sqrre01drp" />
              <span className="text-[9px] font-bold text-navy-950" data-id="element-1279" data-mp="1e9ng3qi4lvan">{name}</span>
            </button>
          </li>)}
      </ul>

      {/* Recipe builder */}
      <div className="rounded-2xl border-2 border-blue-500 bg-blue-50 px-3 py-2.5" data-id="element-1280" data-mp="1f41gu4c1wa0h">
        <div className="flex items-center justify-between gap-2" data-id="element-1281" data-mp="1f65kqm9y990r">
          <p className="text-[9px] font-bold uppercase tracking-[.12em] text-blue-700" data-id="element-1282" data-mp="1k2cs8jhzuskt">Build your recipe</p>
          <button type="button" onClick={feelLucky} className="flex shrink-0 items-center gap-1 rounded-full bg-navy-950 px-2 py-0.5 text-[8px] font-bold text-blue-200 transition-colors duration-200 hover:bg-navy-950/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400" data-id="element-1283" data-mp="1o6anuk841l94">
            <DicesIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1284" data-mp="27sqj46oueldt" /> I'm feeling lucky
          </button>
        </div>

        <div className="mt-1.5 flex items-center gap-1.5" data-id="element-1285" data-mp="2butxp46t663b">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{
          backgroundColor: availableFlavors[base][1]
        }} aria-hidden="true" data-id="element-1286" data-mp="2bs8uaaqv3i3g" />
          <p className="truncate text-[11px] font-bold text-navy-950" data-id="element-1287" data-mp="56kwapwo0rti">{lucky ? 'Sparkling Cold Brew' : availableFlavors[base][0]}</p>
        </div>

        {lucky && <div className="mt-1.5 flex items-start gap-1.5 rounded-xl bg-navy-950 px-2.5 py-2 text-white [animation:pd3-rise-in_.35s_ease-out]" data-id="element-1288" data-mp="9barlced0ay">
            <SparklesIcon className="mt-0.5 h-3 w-3 shrink-0 text-blue-300" aria-hidden="true" data-id="element-1289" data-mp="3ncfy5oryt7ls" />
            <p className="text-[9px] font-semibold leading-snug text-blue-100" data-id="element-1290" data-mp="nwsviwx8uy4f">
              Only JuLi™ can do this — cold brew carbonated to order. Nobody sells it in a can.
            </p>
          </div>}

        {/* Water type */}
        <div className="mt-2" data-id="element-1291" data-mp="f24qn7vkzig5">
          <p className="text-[8px] font-bold uppercase tracking-[.08em] text-slate-500" data-id="element-1292" data-mp="a5e90g44oesm">Water type</p>
          <div className="mt-1 grid grid-cols-3 gap-1" data-id="element-1293" data-mp="17ka46uigswu0">
            {waterTypes.map((type, index) => <button key={type} type="button" onClick={() => touch(() => setWater(index))} aria-pressed={water === index} className={`rounded-lg py-1.5 text-[9px] font-bold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${water === index ? 'bg-blue-500 text-white' : 'bg-white text-slate-500 hover:text-navy-950'}`} data-id="element-1294" data-mp="12ta3rfa4zwmr" data-mpi={index}>
                {type}
              </button>)}
          </div>
        </div>

        {/* Fizz — only when sparkling */}
        {isSparkling && <div className="mt-1.5 [animation:pd3-rise-in_.3s_ease-out]" data-id="element-1295" data-mp="17zgo1wxzcb4f">
            <p className="text-[8px] font-bold uppercase tracking-[.08em] text-slate-500" data-id="element-1296" data-mp="tze84g98frrt">How bubbly</p>
            <div className="mt-1 grid grid-cols-3 gap-1" data-id="element-1297" data-mp="xzprzqtrto28">
              {fizzLevels.map((level, index) => <button key={level} type="button" onClick={() => touch(() => setFizz(index))} aria-pressed={fizz === index} className={`flex items-center justify-center gap-0.5 rounded-lg py-1.5 text-[9px] font-bold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${fizz === index ? 'bg-navy-950 text-blue-200' : 'bg-white text-slate-500 hover:text-navy-950'}`} data-id="element-1298" data-mp="2odypaiych56g" data-mpi={index}>
                  <span aria-hidden="true" data-id="element-1299" data-mp="lop3qkbh182v">{'•'.repeat(index + 1)}</span> {level}
                </button>)}
            </div>
          </div>}

        {/* Temperature — hidden for hot water */}
        {!isHot && <div className="mt-1.5" data-id="element-1300" data-mp="19fufooeajg7w">
            <p className="text-[8px] font-bold uppercase tracking-[.08em] text-slate-500" data-id="element-1301" data-mp="19err4n4fxpv5">Temperature</p>
            <div className="mt-1 grid grid-cols-3 gap-1" data-id="element-1302" data-mp="14hszcb891v04">
              {chillLevels.map((level, index) => <button key={level} type="button" onClick={() => touch(() => setChill(index))} aria-pressed={chill === index} className={`rounded-lg py-1.5 text-[9px] font-bold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${chill === index ? 'bg-blue-500 text-white' : 'bg-white text-slate-500 hover:text-navy-950'}`} data-id="element-1303" data-mp="3b7z800zpcfj5" data-mpi={index}>
                  {level}
                </button>)}
            </div>
          </div>}

        {/* Stevia */}
        <div className="mt-1.5" data-id="element-1304" data-mp="2p0lmxnqk95kt">
          <p className="text-[8px] font-bold uppercase tracking-[.08em] text-slate-500" data-id="element-1305" data-mp="2txv9mvmkf34y">Stevia</p>
          <div className="mt-1 grid grid-cols-3 gap-1" data-id="element-1306" data-mp="2ywyf2h6gjbdg">
            {steviaLevels.map((level, index) => <button key={level} type="button" onClick={() => touch(() => setStevia(index))} aria-pressed={stevia === index} className={`rounded-lg py-1.5 text-[9px] font-bold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${stevia === index ? 'bg-blue-500 text-white' : 'bg-white text-slate-500 hover:text-navy-950'}`} data-id="element-1307" data-mp="33neto4m0l31e" data-mpi={index}>
                {level}
              </button>)}
          </div>
        </div>

        <p className="mt-2 rounded-lg bg-white px-2 py-1.5 text-[9px] font-semibold text-navy-950" data-id="element-1308" data-mp="1tnu7yg046zxu">{summary}</p>

        <div className="mt-2 grid grid-cols-2 gap-1.5" data-id="element-1309" data-mp="1osyd783htyh1">
          <button type="button" onClick={() => setChoosingSlot(current => !current)} aria-expanded={choosingSlot} className="flex items-center justify-center gap-1 rounded-lg bg-white py-1.5 text-[9px] font-bold text-navy-950 transition-colors duration-200 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400" data-id="element-1310" data-mp="1kq1yhsh2qmid">
            <PlusIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1311" data-mp="10zq0pq1waet1" /> Save & send
          </button>
          <button type="button" onClick={onScan} className="flex items-center justify-center gap-1 rounded-lg bg-blue-500 py-1.5 text-[9px] font-bold text-white transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400" data-id="element-1312" data-mp="x0pgq47ol64g">
            <ScanLineIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1313" data-mp="dfpcu75wlan8" /> Scan & pour
          </button>
        </div>

        {choosingSlot ? <div className="mt-1.5 [animation:pd3-rise-in_.3s_ease-out]" data-id="element-1314" data-mp="161rzrnpkc27s">
            <p className="text-[8px] font-bold uppercase tracking-[.08em] text-slate-500" data-id="element-1315" data-mp="1a3vlr69uyp7b">Save this for</p>
            <div className="mt-1 grid grid-cols-3 gap-1" data-id="element-1316" data-mp="wcqi7t3ddcrp">
              {['Morning', 'Afternoon', 'Evening'].map(slot => <button key={slot} type="button" onClick={() => {
            setRecipes(current => [...current.filter(recipe => recipe.slot !== slot), {
              id: Date.now(),
              slot,
              name: availableFlavors[base][0],
              color: availableFlavors[base][1],
              summary,
              shared: false
            }]);
            setChoosingSlot(false);
          }} className="rounded-lg bg-navy-950 py-1.5 text-[9px] font-bold text-blue-100 transition-colors duration-200 hover:bg-navy-950/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400" data-id="element-1317" data-mp="11as40ls6jq6v">
                  {slot}
                </button>)}
            </div>
          </div> : <p className="mt-1.5 text-[8px] leading-snug text-slate-500" data-id="element-1318" data-mp="1p76kf1lb1uv8">
            Saved recipes follow you to any endpoint, no re-entry needed.
          </p>}
      </div>

      {/* Saved recipes */}
      <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5" data-id="element-1319" data-mp="30mlh049lqkpt">
        <div className="flex items-center justify-between gap-2" data-id="element-1320" data-mp="yx6227wq47sb">
          <p className="text-[9px] font-bold uppercase tracking-[.12em] text-blue-700" data-id="element-1321" data-mp="yutb558zmn34">My recipes</p>
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[7px] font-bold text-slate-500" data-id="element-1322" data-mp="12wewlezl89ox">By time of day</span>
        </div>

        <ul className="mt-1.5 flex flex-col gap-1" data-id="element-1323" data-mp="1couekcavwy9t">
          {['Morning', 'Afternoon', 'Evening'].map(slot => {
          const recipe = recipes.find(item => item.slot === slot);
          if (!recipe) {
            return <li key={slot} className="flex items-center justify-between gap-2 rounded-lg border border-dashed border-slate-200 px-2 py-1.5" data-id="element-1324" data-mp="1mkxfjeu02qv6">
                  <span className="text-[9px] font-bold text-slate-400" data-id="element-1325" data-mp="1vmwxc9cu9yz6">{slot}</span>
                  <span className="text-[8px] text-slate-400" data-id="element-1326" data-mp="1vksrvmzakrws">Not set</span>
                </li>;
          }
          return <li key={slot} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 [animation:pd3-rise-in_.3s_ease-out]" data-id="element-1327" data-mp="1a70pjv36bo3">
                <span className="flex min-w-0 items-center gap-1.5" data-id="element-1328" data-mp="3l3servu0zymo">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{
                backgroundColor: recipe.color
              }} aria-hidden="true" data-id="element-1329" data-mp="3l2hazw0kyqvk" />
                  <span className="min-w-0" data-id="element-1330" data-mp="3b6mouqx5edo1">
                    <span className="block truncate text-[9px] font-bold text-navy-950" data-id="element-1331" data-mp="3b7xu6v68rth9">
                      {slot} · {recipe.name}
                    </span>
                    <span className="block truncate text-[8px] text-slate-500" data-id="element-1332" data-mp="7p4kitgl3osd">{recipe.summary}</span>
                  </span>
                </span>
                <button type="button" onClick={() => setRecipes(current => current.map(item => item.slot === slot ? {
              ...item,
              shared: !item.shared
            } : item))} aria-pressed={recipe.shared} className={`flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[7px] font-bold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${recipe.shared ? 'bg-blue-500 text-white' : 'bg-white text-slate-600 hover:text-navy-950'}`} data-id="element-1333" data-mp="2se4g64s4t6y">
                  {recipe.shared ? <CheckIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1334" data-mp="1xvy3fvtdw37b" /> : <Share2Icon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1335" data-mp="1xvekqcvwtas8" />}
                  {recipe.shared ? 'Shared' : 'Share'}
                </button>
              </li>;
        })}
        </ul>

        <p className="mt-1.5 text-[8px] leading-snug text-slate-500" data-id="element-1336" data-mp="1o3s0jr1lg1dc">
          Shared recipes spread through the office — and travel to every endpoint on the network.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-id="element-1337" data-mp="1f7177meff0mg">
        <div className="flex items-center justify-between gap-2" data-id="element-1338" data-mp="1a5k91ynbts9k">
          <p className="text-[9px] font-bold uppercase tracking-[.12em] text-blue-700" data-id="element-1339" data-mp="156h6ckutjy4r">Ask your office for</p>
          <span className="rounded-full bg-white px-1.5 py-0.5 text-[7px] font-bold text-slate-500" data-id="element-1340" data-mp="3gtatxundpvk0">29 flavors available</span>
        </div>

        <ul className="mt-1.5 flex flex-col gap-1" data-id="element-1341" data-mp="3lvjr737abjrx">
          {requests.map(([name, votes], index) => <li key={name} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5" data-id="element-1342" data-mp="3qqpadu9aykcb" data-mpi={index}>
              <span className="min-w-0" data-id="element-1343" data-mp="2y4e88f61sbde">
                <span className="block truncate text-[10px] font-bold text-navy-950" data-id="element-1344" data-mp="2y5peclmytv7o">{name}</span>
                <span className="block text-[8px] text-slate-500" data-id="element-1345" data-mp="3337dxlf3ey30">{votes} colleagues requested</span>
              </span>
              <span className={`flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[7px] font-bold ${index === 0 ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-1346" data-mp="372pz8iattb6d">
                {index === 0 ? <ThumbsUpIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1347" data-mp="2fbk2mbubdvlu" /> : <PlusIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1348" data-mp="2jdf5hkrw8h1q" />}
                {index === 0 ? 'Voted' : 'Add'}
              </span>
            </li>)}
        </ul>

        <p className="mt-1.5 text-[8px] leading-snug text-slate-500" data-id="element-1349" data-mp="3hpri3oy3s7b5">
          Requests go straight to facilities — and tell us what to stock next.
        </p>
      </div>

      {/* Member shop */}
      <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5" data-id="element-1350" data-mp="3rhnfexsd7teb">
        <div className="flex items-center justify-between gap-2" data-id="element-1351" data-mp="33qi1io5zjb6n">
          <p className="text-[9px] font-bold uppercase tracking-[.12em] text-blue-700" data-id="element-1352" data-mp="33suqvme2ontq">Shop</p>
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[7px] font-bold text-slate-500" data-id="element-1353" data-mp="2yu9r1o2qg65t">Ships to you</span>
        </div>

        <ul className="mt-1.5 flex flex-col gap-1" data-id="element-1354" data-mp="38pl481yht5bg">
          {shopItems.map(([name, meta, price]) => {
          const inCart = cart.includes(name);
          return <li key={name} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5" data-id="element-1355" data-mp="nzvsvyx001ni">
                <span className="min-w-0" data-id="element-1356" data-mp="vida6rb48kr1">
                  <span className="block truncate text-[10px] font-bold text-navy-950" data-id="element-1357" data-mp="vlr8nogns9t7">{name}</span>
                  <span className="block truncate text-[8px] text-slate-500" data-id="element-1358" data-mp="10g4zddkdlsrj">{meta}</span>
                </span>
                <button type="button" onClick={() => setCart(current => inCart ? current.filter(item => item !== name) : [...current, name])} aria-pressed={inCart} className={`flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[7px] font-bold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${inCart ? 'bg-blue-500 text-white' : 'bg-white text-slate-600 hover:text-navy-950'}`} data-id="element-1359" data-mp="qkbl9pr0dr7n">
                  {inCart ? <CheckIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1360" data-mp="1x7yu0pnjapfs" /> : <ShoppingBagIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-1361" data-mp="21z747nwoueu0" />}
                  {inCart ? 'Added' : price}
                </button>
              </li>;
        })}
        </ul>

        <p className="mt-1.5 text-[8px] leading-snug text-slate-500" data-id="element-1362" data-mp="9bj32rl7wv7s">
          {cart.length > 0 ? `${cart.length} item${cart.length > 1 ? 's' : ''} in cart · charged to your membership.` : 'Members buy cartridges and gear for home — a second revenue line per member.'}
        </p>
      </div>
    </div>;
}